import type { PickupStationParam, ReservationInfo, TravelerFormValues } from '../api/reservationInfo';

export const SHINKANSEN_ADDRESS_TYPES = {
  select: 'select',
  text: 'text',
  organizationBaseSelect: 'organization_base_select'
} as const;

export type ShinkansenAddressType = typeof SHINKANSEN_ADDRESS_TYPES[keyof typeof SHINKANSEN_ADDRESS_TYPES];

export const SHINKANSEN_ADDRESS_TYPE_LABELS: Record<ShinkansenAddressType, string> = {
  text: '手動入力',
  select: '部署から選択',
  organization_base_select: '拠点から選択'
};

export const PREFERRED_TICKETING_METHODS = {
  qr: 'qr',
  postal: 'postal'
} as const;

export type PreferredTicketingMethod =
  typeof PREFERRED_TICKETING_METHODS[keyof typeof PREFERRED_TICKETING_METHODS];

export const PREFERRED_TICKETING_METHOD_LABELS: Record<PreferredTicketingMethod, string> = {
  qr: 'QR発券（QRコードで受け取る）',
  postal: 'チケット郵送（自宅・職場に届けてもらう）'
};

export type ShinkansenFormValues = {
  shinkansenAddressType: ShinkansenAddressType;
  postcode: string;
  shinkansenAddress: string;
  shinkansenAddressee: string;
  shinkansenTicketType: 'normal' | 'kyuusyuu' | 'bulk_ticket' | 'ex' | 'business_ekinet';
  shinkansenSeatType: 'free' | 'reserve' | 'green';
  shinkansenSeat: '指定なし' | '窓側' | '通路側' | 'その他' | '';
  shinkansenSeatDetail: string;
  exicNumber: string;
  exicPassword: string;
  exicCardId: number | null;
  departmentId: number | null;
  organizationBaseId: number | null;
  organizationAddressId: number | null;
  preferredTicketingMethod: PreferredTicketingMethod | '';
};

export type CommonFormValues = {
  finalDestination: string;
  purpose: string;
  internalNumber: string;
  chargingDepartmentId: number | null;
  projectId: number | null;
};

export type CommonErrors = Partial<Record<keyof CommonFormValues, string>>;

export type FormValues = {
  finalDestination: string;
  purpose: string;
  internalNumber: string;
  chargingDepartmentId: number | null;
  projectId: number | null;
  traveler: TravelerFormValues | null;
  shinkansen: ShinkansenFormValues | null;
  pickupStations: PickupStationParam[];
};

export const isShinkansenPostalTicketing = (
  shinkansen: ShinkansenFormValues,
  settings: NonNullable<ReservationInfo['shinkansenInfo']>['settings']
): boolean => {
  if (settings.useQr && settings.usePostal) {
    return shinkansen.preferredTicketingMethod === 'postal';
  }
  return settings.usePostal && !settings.useQr;
};

export const isShinkansenQrTicketing = (
  shinkansen: ShinkansenFormValues,
  settings: NonNullable<ReservationInfo['shinkansenInfo']>['settings']
): boolean => {
  if (shinkansen.shinkansenTicketType === 'ex' || shinkansen.shinkansenTicketType === 'business_ekinet') {
    return false;
  }
  if (settings.useQr && settings.usePostal) {
    return shinkansen.preferredTicketingMethod === 'qr' || shinkansen.preferredTicketingMethod === '';
  }
  return !!settings.useQr;
};

export const requiresPickupStationSelection = (
  shinkansen: ShinkansenFormValues,
  settings: NonNullable<ReservationInfo['shinkansenInfo']>['settings'],
  pickupStationOptionsCount: number
): boolean => {
  if (!isShinkansenQrTicketing(shinkansen, settings)) {
    return false;
  }
  if (shinkansen.shinkansenTicketType !== 'normal') {
    return false;
  }
  return pickupStationOptionsCount > 0;
};
